import { getSelectedFontFamily } from "./fontUtils";

import {
  LOGO_OPTIONS,
  FONT_FAMILY_OPTIONS,
  DEFAULT_BACKGROUND_TYPE,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_BACKGROUND_GRADIENT_START_COLOR,
  DEFAULT_BACKGROUND_GRADIENT_END_COLOR,
  DEFAULT_BACKGROUND_GRADIENT_ANGLE,
  DEFAULT_PAGE_FONT_SIZE,
  MIN_PAGE_FONT_SIZE,
  DEFAULT_PAGE_FONT_FAMILY,
  DEFAULT_FEATURE_SYMBOL_COLOR,
  DEFAULT_FEATURE_SYMBOL_BACKGROUND_COLOR,
  DEFAULT_FEATURE_SYMBOL_SHAPE,
  DEFAULT_CTA_FONT_SIZE,
  MIN_CTA_FONT_SIZE,
  DEFAULT_CTA_BORDER_RADIUS,
  MIN_CTA_BORDER_RADIUS,
} from "./constants";

// ============================================================
// SELECTED LOGO
// ============================================================

export const getSelectedLogo = (logoValue) => {
  return LOGO_OPTIONS.find((logo) => logo.value === logoValue);
};

// ============================================================
// CTA FONT SIZE
// ============================================================

export const getValidCtaFontSize = (fontSize) => {
  if (fontSize === "" || fontSize == null) {
    return DEFAULT_CTA_FONT_SIZE;
  }

  const value = Number(fontSize);

  if (!Number.isFinite(value) || value < MIN_CTA_FONT_SIZE) {
    return DEFAULT_CTA_FONT_SIZE;
  }

  return value;
};

// ============================================================
// CTA BORDER RADIUS
// ============================================================

export const getValidCtaBorderRadius = (borderRadius) => {
  if (borderRadius === "" || borderRadius == null) {
    return DEFAULT_CTA_BORDER_RADIUS;
  }

  const value = Number(borderRadius);

  if (!Number.isFinite(value) || value < MIN_CTA_BORDER_RADIUS) {
    return DEFAULT_CTA_BORDER_RADIUS;
  }

  return value;
};

// ============================================================
// FEATURE SYMBOL STYLE
// ============================================================

export const getFeatureSymbolStyle = (symbol = {}) => {
  const shape = symbol?.shape || DEFAULT_FEATURE_SYMBOL_SHAPE;

  const color = symbol?.color || DEFAULT_FEATURE_SYMBOL_COLOR;

  if (shape === "none") {
    return {
      color,
      backgroundColor: "transparent",
      borderRadius: 0,
    };
  }

  const backgroundColor =
    symbol?.background_color || DEFAULT_FEATURE_SYMBOL_BACKGROUND_COLOR;

  switch (shape) {
    case "square":
      return {
        color,
        backgroundColor,
        borderRadius: 0,
      };

    case "rounded":
      return {
        color,
        backgroundColor,
        borderRadius: "4px",
      };

    case "circle":
    default:
      return {
        color,
        backgroundColor,
        borderRadius: "50%",
      };
  }
};

// ============================================================
// PAGE FONT SIZE
// ============================================================

export const getValidPageFontSize = (fontSize) => {
  if (fontSize === "" || fontSize == null) {
    return DEFAULT_PAGE_FONT_SIZE;
  }

  const value = Number(fontSize);

  if (!Number.isFinite(value) || value < MIN_PAGE_FONT_SIZE) {
    return DEFAULT_PAGE_FONT_SIZE;
  }

  return value;
};

// ============================================================
// PAGE FONT FAMILY
// ============================================================

export const getPageFontFamily = (fontFamily) => {
  return (
    getSelectedFontFamily(fontFamily)?.css || "Arial, Helvetica, sans-serif"
  );
};

// ============================================================
// PAGE CSS
// ============================================================

export const getPageCss = (page = {}) => {
  const backgroundType = page.background_type || DEFAULT_BACKGROUND_TYPE;

  let background = page.background_color || DEFAULT_BACKGROUND_COLOR;

  if (backgroundType === "linear") {
    const startColor =
      page.background_gradient?.start_color ||
      DEFAULT_BACKGROUND_GRADIENT_START_COLOR;

    const endColor =
      page.background_gradient?.end_color ||
      DEFAULT_BACKGROUND_GRADIENT_END_COLOR;

    const angle =
      page.background_gradient?.angle ?? DEFAULT_BACKGROUND_GRADIENT_ANGLE;

    background = `linear-gradient(${angle}deg, ${startColor}, ${endColor})`;
  }

  if (backgroundType === "radial") {
    const startColor =
      page.background_gradient?.start_color ||
      DEFAULT_BACKGROUND_GRADIENT_START_COLOR;

    const endColor =
      page.background_gradient?.end_color ||
      DEFAULT_BACKGROUND_GRADIENT_END_COLOR;

    background = `radial-gradient(circle, ${startColor}, ${endColor})`;
  }

  const fontSize = getValidPageFontSize(page.font_size);

  const fontFamily = getPageFontFamily(page.font_family);

  return {
    background,

    "--checkout-font-size": `${fontSize}px`,
    "--checkout-font-family": fontFamily,
  };
};
